// src/pages/public/ElectionsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import PublicLayout from '../../../components/layout/PublicLayout';
import Button from '../../../components/common/Button';

const ElectionsPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/schools`);
      const data = await response.json();
      if (data.success) {
        setSchools(data.data);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSchools = schools.filter(school =>
    school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    school.shortName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PublicLayout>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.5); }
          50% { box-shadow: 0 0 40px rgba(239, 68, 68, 0.8); }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-slide-up {
          animation: slideInUp 0.6s ease-out forwards;
        }
        
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 2s infinite;
        }
        
        .animate-glow {
          animation: glow 2s ease-in-out infinite;
        }

        .gradient-text {
          background: linear-gradient(135deg, #EF4444 0%, #DC2626 50%, #991B1B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .card-hover-effect {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-hover-effect:hover {
          transform: translateY(-8px) scale(1.02);
        }
      `}</style>

      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-gray-900 via-black to-red-950 overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(239, 68, 68) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        {/* Floating Red Orbs */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-red-600 rounded-full opacity-20 blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-red-500 rounded-full opacity-20 blur-3xl animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-40 right-1/4 w-24 h-24 bg-black rounded-full opacity-30 blur-2xl animate-float" style={{ animationDelay: '0.5s' }}></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Admin Portal Button */}
          <div className=" top-0 right-8 animate-slide-up">
            <Link to="/admin/election-login">
              <Button variant="outline" size="lg" className="group relative px-8 py-4 bg-transparent border-2 border-white/30 text-white font-semibold rounded-lg backdrop-blur-sm transition-all duration-300 hover:border-yellow-500 hover:bg-yellow-600/10 hover:scale-105">
                🔐 Admin Portal
              </Button>
            </Link>
            {/* <Link to="/admin/election-login">
              <Button 
                variant="outline" 
                size="lg" 
                className="group relative px-8 py-4 bg-gradient-to-r from-red-600/10 to-black/20 border-2 border-red-500/30 text-white font-semibold rounded-xl backdrop-blur-sm transition-all duration-300 hover:border-red-500 hover:bg-red-600/20 hover:scale-105 hover:shadow-lg hover:shadow-red-500/50"
              >
                <span className="flex items-center gap-2">
                  🔐 <span>Admin Portal</span>
                </span>
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-red-600/0 to-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Button>
            </Link> */}
          </div>

          <div className="text-center mb-16 animate-slide-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-red-900/30 to-black/40 backdrop-blur-md border border-red-500/30 rounded-full mb-8 shadow-lg animate-glow">
              <div className="relative">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
              </div>
              <span className="text-red-400 text-sm font-semibold tracking-wide">SECURE DIGITAL VOTING PLATFORM</span>
            </div>

            {/* Main Title */}
            <h1 className="text-5xl md:text-7xl font-black mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <span className="gradient-text">🗳️ Election Hub</span>
            </h1>
            
            <div className="relative inline-block animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Empowering democratic participation through 
                <span className="text-red-400 font-bold"> secure</span>, 
                <span className="text-red-400 font-bold"> transparent</span>, and 
                <span className="text-red-400 font-bold"> innovative</span> digital elections
              </p>
            </div>

            {/* Stats Bar */}
            <div className="flex flex-wrap justify-center gap-8 mt-12 animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-500">{schools.length}+</div>
                <div className="text-sm text-gray-400 mt-1">Active Schools</div>
              </div>
              <div className="h-12 w-px bg-red-500/30"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-500">100%</div>
                <div className="text-sm text-gray-400 mt-1">Secure</div>
              </div>
              <div className="h-12 w-px bg-red-500/30"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-500">24/7</div>
                <div className="text-sm text-gray-400 mt-1">Available</div>
              </div>
            </div>
          </div>

          {/* Enhanced Search Bar */}
          <div className="max-w-3xl mx-auto animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-black rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="🔍 Search for your school or institution..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-8 py-5 bg-white/5 backdrop-blur-xl border-2 border-red-500/30 rounded-2xl text-white text-lg placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-red-500/50 focus:border-red-500 transition-all duration-300"
                />
                <div className="absolute right-6 top-1/2 transform -translate-y-1/2">
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schools List Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-32">
              <div className="relative inline-flex items-center justify-center">
                <div className="absolute animate-spin rounded-full h-24 w-24 border-t-4 border-b-4 border-red-600"></div>
                <div className="absolute animate-spin rounded-full h-24 w-24 border-r-4 border-l-4 border-black" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                <div className="text-4xl">🗳️</div>
              </div>
              <p className="mt-8 text-xl font-semibold text-gray-700">Loading Schools...</p>
              <p className="mt-2 text-gray-500">Please wait while we fetch available elections</p>
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="text-center py-32 animate-slide-up">
              <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-red-100 to-gray-200 mb-8">
                <svg className="w-16 h-16 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">No Schools Found</h3>
              <p className="text-lg text-gray-600 mb-6">We couldn't find any schools matching your search</p>
              <button 
                onClick={() => setSearchTerm('')}
                className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold rounded-xl hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Clear Search
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Available Elections
                </h2>
                <p className="text-gray-600">
                  Found <span className="text-red-600 font-bold">{filteredSchools.length}</span> {filteredSchools.length === 1 ? 'school' : 'schools'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredSchools.map((school, index) => (
                  <Link
                    key={school.id}
                    to={`/elections/${school.id}/vote`}
                    className="group animate-slide-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="card-hover-effect bg-white rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden border-2 border-gray-200 hover:border-red-500 relative">
                      {/* Shimmer Effect on Hover */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-shimmer pointer-events-none"></div>
                      
                      {/* Red Corner Accent */}
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-600 to-transparent opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
                      
                      {/* School Logo/Header */}
                      <div className="h-48 bg-gradient-to-br from-red-600 via-red-700 to-black flex items-center justify-center relative overflow-hidden">
                        {school.logo ? (
                          <img 
                            src={school.logo} 
                            alt={school.name} 
                            className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" 
                          />
                        ) : (
                          <div className="text-white text-7xl font-black drop-shadow-2xl group-hover:scale-110 transition-transform duration-300">
                            {school.shortName || school.name.charAt(0)}
                          </div>
                        )}
                        
                        {/* Overlay Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Vote Icon on Hover */}
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
                            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* School Info */}
                      <div className="p-6 relative">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-red-600 transition-colors duration-300">
                          {school.name}
                        </h3>
                        
                        {school.shortName && (
                          <p className="text-sm text-gray-500 mb-4 font-medium">{school.shortName}</p>
                        )}
                        
                        <div className="flex items-center justify-between mt-6">
                          <span className="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-300">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                            ACTIVE ELECTION
                          </span>
                          
                          <span className="flex items-center text-red-600 font-bold group-hover:translate-x-2 transition-transform duration-300">
                            Vote Now
                            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                        </div>

                        {/* Bottom Accent Bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 via-black to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 via-red-700 to-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your Voice Matters
          </h2>
          <p className="text-red-100 text-lg mb-8">
            Join thousands of students participating in democratic elections across institutions
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-2xl font-bold text-white">Secure</div>
              <div className="text-red-200 text-sm">End-to-end encryption</div>
            </div>
            <div className="px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-2xl font-bold text-white">Fast</div>
              <div className="text-red-200 text-sm">Quick voting process</div>
            </div>
            <div className="px-6 py-3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-2xl font-bold text-white">Transparent</div>
              <div className="text-red-200 text-sm">Real-time results</div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ElectionsPage;